import scorableItemReader from '../../store/readHelpers/ScorableItemReader';
import tocReader from '../../store/readHelpers/TocReader';
import navigationService from '../NavigationService';
import { t } from '../i18nService';
import storeManager from '../../store/StoreManager';
import responseService from '../ResponseService';
import settingsReader from '../../store/readHelpers/SettingsReader';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import numberingSchemes from '../../constants/NumberingSchemes';
import customFieldsService from '../CustomFieldsService';
import customFieldsConstants from '../../constants/CustomFieldsConstants';
import uiReader from '../../store/readHelpers/uiReader';
import uiService from '../../services/UIService';

const scorableItemNumberingService = {
    useNumberPerInteraction() {
        return scorableItemReader.useNumberPerInteraction();
    },

    getScorableItemsInQuestion(contentRevisionId) {
        return scorableItemReader.getScorableItemsInQuestion(contentRevisionId) || [];
    },

    getScorableItemsInInteraction(contentRevisionId, responseIdentifier) {
        return scorableItemReader.getScorableItemsInInteraction(contentRevisionId, responseIdentifier) || [];
    },

    getScorableItemsInSection(section) {
        const scorableItemsInSection = [];
        section.questions.forEach((question) => {
            scorableItemsInSection.push(...this.getScorableItemsInQuestion(question.contentRevisionId));
        });
        return scorableItemsInSection;
    },

    getFormattedItemNumber(scorables) {
        if (Array.isArray(scorables) && scorables.length) {
            const { scorableItemsDash = '-' } = storeManager.getState().ui.variables;
            return `${scorables[0].orderNumber}${scorables.length > 1 ? `${scorableItemsDash}${scorables[scorables.length - 1].orderNumber}` : ''}`;
        }
        if (uiService.isPaperBasedRenderForCEQ()) {
            return '1';
        }
        return undefined;
    },

    getInteractionNumber(contentRevisionId, responseIdentifier) {
        if (!scorableItemNumberingService.useNumberPerInteraction()) {
            return undefined;
        }
        const items = scorableItemReader.getScorableItemsInInteraction(contentRevisionId, responseIdentifier);
        return this.getFormattedItemNumber(items);
    },

    getOrderNumberOfFirstScorableItem(contentRevisionId, responseIdentifier) {
        if (!scorableItemNumberingService.useNumberPerInteraction()) {
            return undefined;
        }

        const scorableItem = this.getScorableItemsInQuestion(contentRevisionId)
            .find(item => (item.responseIdentifier === responseIdentifier));

        return scorableItem ? scorableItem.orderNumber : undefined;
    },

    isActiveScorableItem(interactionNumber) {
        const currentScorableItem = storeManager.getState().currentlyShown.scorableItem;
        if (!currentScorableItem || !scorableItemNumberingService.useNumberPerInteraction() || !interactionNumber) {
            return false;
        }
        if (interactionNumber.includes('-')) {
            const interval = interactionNumber.split('-');
            return currentScorableItem >= parseInt(interval[0], 10) && currentScorableItem <= parseInt(interval[1], 10);
        }
        return currentScorableItem === parseInt(interactionNumber, 10);
    },

    isFlaggedScorableItem(identifier) {
        const { itemFlag: { flaggedItems } } = storeManager.getState();
        return flaggedItems.includes(identifier);
    },

    isValidOrderNumber(orderNumber, contentRevisionId) {
        if (isNaN(orderNumber)) {
            return false;
        }
        return this.getScorableItemsInQuestion(contentRevisionId).some(item => item.orderNumber === orderNumber);
    },

    isAttemptedScorableItem(contentRevisionId, scorableItem, itemResult) {
        if (!itemResult) {
            return false;
        }

        const itemResultForIdentifier = itemResult.responseIdentifiers.find(item => item.identifier === scorableItem.responseIdentifier);
        if (!itemResultForIdentifier) {
            return false;
        }

        let isAttempted = false;

        if (itemResultForIdentifier.baseType === 'directedPair') {
            isAttempted = !!itemResultForIdentifier.userResponse.find(item => item.includes(scorableItem.choiceIdentifier));
        } else if (scorableItem.firstCollapsedItem || scorableItem.hidden) {
            isAttempted = itemResultForIdentifier.userResponse.length >= scorableItem.collapsedItemIndex + 1;
        } else {
            const isEmptyResponse = itemResultForIdentifier.userResponse.every(
                response => responseService.emptyResponses.some(emptyItem => response === emptyItem)
            );
            isAttempted = itemResultForIdentifier.userResponse.length > 0 && !isEmptyResponse;
        }

        return isAttempted;
    },

    isLastScorableItemInQuestion(contentRevisionId, responseIdentifier) {
        const lastItem = this.getLastScorableItemInQuestion(contentRevisionId);
        return lastItem && !!scorableItemReader.getScorableItemsInInteraction(contentRevisionId, responseIdentifier)
            .find(currentItem => (currentItem.contentRevisionId === lastItem.contentRevisionId && currentItem.orderNumber === lastItem.orderNumber));
    },

    isLastScorableItemInQuestionWithChoice(contentRevisionId, responseIdentifier, choiceIdentifier) {
        const lastItem = this.getLastScorableItemInQuestion(contentRevisionId);
        return lastItem && !!scorableItemReader.getScorableItemsInInteractionWithChoice(contentRevisionId, responseIdentifier, choiceIdentifier)
            .find(currentItem => (currentItem.contentRevisionId === lastItem.contentRevisionId && currentItem.orderNumber === lastItem.orderNumber));
    },

    getFirstScorableItemInQuestion(contentRevisionId) {
        const items = this.getScorableItemsInQuestion(contentRevisionId);
        return items.length ? items[0] : undefined;
    },

    getFirstValidOrderNumberInQuestion(contentRevisionId) {
        const item = this.getFirstScorableItemInQuestion(contentRevisionId);
        if (item) {
            return item.orderNumber;
        }
        return undefined;
    },

    getLastNavigatableScorableItemInQuestion(contentRevisionId) {
        const items = this.getScorableItemsInQuestion(contentRevisionId);
        if (items.length) {
            const lastItem = items[items.length - 1];
            if (lastItem.hidden) {
                return items.find(item => item.responseIdentifier === lastItem.responseIdentifier && item.collapsedItemIndex === 0);
            }
            return lastItem;
        }
        return undefined;
    },

    getLastScorableItemInQuestion(contentRevisionId) {
        const items = this.getScorableItemsInQuestion(contentRevisionId);
        if (items.length) {
            return items[items.length - 1];
        }
        return undefined;
    },

    getLastValidOrderNumberInQuestion(contentRevisionId) {
        const item = this.getLastScorableItemInQuestion(contentRevisionId);
        return item ? item.orderNumber : undefined;
    },

    getLastValidOrderNumber() {
        const scorableArray = scorableItemReader.getScorableItems();
        return scorableArray?.length ? scorableArray[scorableArray.length - 1].orderNumber : undefined;
    },

    getPreviousScorableItem(contentRevisionId, orderNumber) {
        const scorableArray = scorableItemReader.getScorableItems();
        const index = scorableArray.findIndex((item) => {
            return item.contentRevisionId === contentRevisionId && item.orderNumber === parseInt(orderNumber, 10);
        });

        let prevIndex = index - 1;
        while (scorableArray[prevIndex] && scorableArray[prevIndex].hidden) {
            prevIndex--;
        }

        if (prevIndex >= 0) {
            return scorableArray[prevIndex];
        }

        return undefined;
    },

    getNextScorableItem(contentRevisionId, orderNumber) {
        const scorableArray = scorableItemReader.getScorableItems();
        const index = scorableArray.findIndex((item) => {
            return item.contentRevisionId === contentRevisionId && item.orderNumber === parseInt(orderNumber, 10);
        });

        let nextIndex = index + 1;
        while (scorableArray[nextIndex] && scorableArray[nextIndex].hidden) {
            nextIndex++;
        }

        return (nextIndex <= scorableArray.length - 1) ? scorableArray[nextIndex] : undefined;
    },

    getInteractionChoiceNumber(contentRevisionId, responseIdentifier, choiceIdentifier) {
        if (!scorableItemNumberingService.useNumberPerInteraction()) {
            return undefined;
        }

        const item = this.getScorableItemsInQuestion(contentRevisionId).find((currentItem) => {
            return currentItem.responseIdentifier === responseIdentifier && currentItem.choiceIdentifier === choiceIdentifier;
        });

        return item ? this.getFormattedItemNumber([item]) : undefined;
    },

    getQuestionNumber(contentRevisionId) {
        const scorables = this.getScorableItemsInQuestion(contentRevisionId);

        return this.getFormattedItemNumber(scorables);
    },

    getQuestionTitle(contentRevisionId) {
        const scorables = this.getScorableItemsInQuestion(contentRevisionId);
        const questionNumber = this.getQuestionNumber(contentRevisionId);

        if (scorables.length > 1) {
            return `${t('generic.multipleQuestionsTitle')} ${questionNumber}`;
        }

        return `${t('generic.singleQuestionTitle')} ${questionNumber}`;
    },

    getSectionTitle(section) {
        const scorables = this.getScorableItemsInSection(section);
        const questionNumber = this.getFormattedItemNumber(scorables);

        if (scorables.length > 1) {
            return `${t('generic.multipleQuestionsTitle')} ${questionNumber}`;
        }

        return `${t('generic.singleQuestionTitle')} ${questionNumber}`;
    },

    getScorableItemsPerResponseId(responseIdentifier, question) {
        const scorables = this.getScorableItemsInQuestion(question.contentRevisionId);
        return scorables.filter((item) => {
            return responseIdentifier === item.responseIdentifier;
        });
    },

    navigateToScorableItem(contentRevisionId, responseIdentifier, choiceIdentifier, skipScroll) {
        if (!scorableItemNumberingService.useNumberPerInteraction()) {
            return;
        }
        const sectionId = tocReader.getSectionIdForQuestionId(contentRevisionId);
        let scorableItemNumber;

        if (choiceIdentifier) {
            scorableItemNumber = this.getInteractionChoiceNumber(contentRevisionId, responseIdentifier, choiceIdentifier);
        } else {
            scorableItemNumber = this.getInteractionNumber(contentRevisionId, responseIdentifier);
        }

        navigationService.navigateToScorableItem(sectionId, contentRevisionId, scorableItemNumber, skipScroll);
    },

    getSectionFormatNumber(sectionId, customFields) {
        const section = assessmentReader.getSectionById(sectionId);
        const partNumber = customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.SECTION);
        const sectionNumber = partNumber || section?.questions?.[0]?.sectionNumber || 1;
        const prefix = uiReader.showSectionPartPrefix() ? t('generic.sectionPartPrefix') : t('generic.sectionNumberPrefix');
        return `${prefix} ${sectionNumber}`;
    },

    getStimulusFormatNumber(sectionId) {
        const numberingScheme = settingsReader.getNumberingScheme();
        const section = assessmentReader.getSectionById(sectionId);
        const questions = section.questions;
        const questionSectionNumber = (questions && questions[0]?.sectionNumber && questions[0].sectionNumber !== 'undefined')
            ? `${questions[0].sectionNumber}`
            : null;

        let stimulusNumber = null;

        switch (numberingScheme) {
        case numberingSchemes.SECTION_ALPHA_NUMERIC:
        case numberingSchemes.SECTION_NUMERIC: {
            stimulusNumber = questionSectionNumber;
            break;
        }
        case numberingSchemes.TEST_SEQUENTIAL: {
            if (questions) {
                const firstQuestionNumber = questions[0].number && questions[0].number !== 'undefined'
                    ? `${questions[0].number}`
                    : null;

                const lastQuestionNumber = questions.length > 1 && questions[questions.length - 1].number
                        && questions[questions.length - 1].number !== 'undefined'
                    ? `${questions[questions.length - 1].number}`
                    : null;

                if (firstQuestionNumber && lastQuestionNumber) {
                    stimulusNumber = `${firstQuestionNumber}-${lastQuestionNumber}`;
                } else {
                    stimulusNumber = firstQuestionNumber || lastQuestionNumber;
                }
            }
            break;
        }
        case numberingSchemes.PER_SCORABLE_ITEM: {
            const state = storeManager.getState();
            const showSectionPartPrefix = state.ui.variables.footer__sectionPartPrefix;
            const prefix = showSectionPartPrefix ? t('generic.sectionPartPrefix') : t('generic.sectionNumberPrefix');

            const customFieldNumber = section.customFields &&
                    customFieldsService.getCustomFieldValue(section.customFields, customFieldsConstants.SECTION);

            if (customFieldNumber) {
                stimulusNumber = `${prefix} ${customFieldNumber}`;
            } else if (questionSectionNumber) {
                stimulusNumber = `${prefix} ${questionSectionNumber}`;
            }
            break;
        }
        default: {
            break;
        }
        }


        return stimulusNumber;
    },

    shouldAddHiddenFlagFromSettings() {
        return settingsReader.isFlaggingEnabled() && scorableItemReader.useNumberPerInteraction();
    },

    shouldAddHiddenFlag(contentRevisionId, responseIdentifier, choiceIdentifier) {
        if (!settingsReader.isFlaggingEnabled() || !scorableItemReader.useNumberPerInteraction()) {
            return false;
        }

        return choiceIdentifier
            ? !scorableItemNumberingService.isLastScorableItemInQuestionWithChoice(contentRevisionId, responseIdentifier, choiceIdentifier)
            : !scorableItemNumberingService.isLastScorableItemInQuestion(contentRevisionId, responseIdentifier);
    },

    shouldAddHiddenFlagForInlineGap() {
        return !(!settingsReader.isFlaggingEnabled() || !scorableItemReader.useNumberPerInteraction());
    },
};

export default scorableItemNumberingService;
